import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, Mail, Lock, ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState(() => localStorage.getItem("remembered_email") || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem("remembered_email"));
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      if (remember) {
        localStorage.setItem("remembered_email", email);
      } else {
        localStorage.removeItem("remembered_email");
      }
      localStorage.removeItem("org_token");
      localStorage.removeItem("org_user");
      toast.success("Giriş başarılı!");
      navigate("/panel");
    } catch (err) {
      toast.error(err.response?.data?.detail || "E-posta veya şifre hatalı");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error("E-posta adresinizi girin"); return; }
    // For now, show a message since email service isn't set up
    setForgotSent(true);
    toast.success("Şifre sıfırlama talimatları e-posta adresinize gönderildi");
  };

  // Forgot password modal
  if (forgotMode) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-stone-100 flex items-center justify-center mx-auto mb-4 rounded-full">
              <Mail className="w-6 h-6 text-[#1B4F72]" />
            </div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Şifremi Unuttum</h1>
            <p className="text-sm text-stone-500 mt-2">Kayıtlı e-posta adresinizi girin, şifre sıfırlama bağlantısı göndereceğiz.</p>
          </div>

          {forgotSent ? (
            <div className="bg-green-50 border border-green-200 p-6 text-center">
              <Mail className="w-8 h-8 text-green-600 mx-auto mb-3" />
              <p className="text-sm text-green-800 font-medium mb-1">E-posta gönderildi!</p>
              <p className="text-xs text-green-600">Şifre sıfırlama talimatları <strong>{forgotEmail}</strong> adresine gönderildi. Lütfen gelen kutunuzu kontrol edin.</p>
              <Button onClick={() => { setForgotMode(false); setForgotSent(false); }} variant="outline" className="mt-4 rounded-none font-mono text-xs uppercase tracking-widest">
                Giriş Sayfasına Dön
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="bg-white border border-stone-200 p-6 space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">E-posta Adresi</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} type="email" required placeholder="firma@ornek.com" className="pl-10 rounded-none border-stone-300" data-testid="forgot-email" />
                </div>
              </div>
              <Button type="submit" className="w-full rounded-none bg-[#1B4F72] hover:bg-[#154360] font-mono text-xs uppercase tracking-widest h-11">
                Sıfırlama Bağlantısı Gönder
              </Button>
            </form>
          )}
          <p className="text-center text-sm text-stone-500 mt-4">
            <button onClick={() => { setForgotMode(false); setForgotSent(false); }} className="text-[#1B4F72] hover:underline font-medium">
              Giriş sayfasına dön
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1B4F72] flex items-center justify-center mx-auto mb-4 rounded-sm">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Firma Girişi</h1>
          <p className="text-sm text-stone-500 mt-1">Firma panelinize erişmek için giriş yapın</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-stone-200 p-6 space-y-4">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">E-posta</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="firma@ornek.com" className="pl-10 rounded-none border-stone-300" data-testid="login-email" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Şifre</Label>
              <button type="button" onClick={() => setForgotMode(true)} className="text-[10px] text-[#1B4F72] hover:underline font-medium" data-testid="forgot-password-link">
                Şifremi Unuttum
              </button>
            </div>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPw ? "text" : "password"}
                required
                placeholder="••••••"
                className="pl-10 pr-10 rounded-none border-stone-300"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                data-testid="toggle-password"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Remember me */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="remember"
              checked={remember}
              onCheckedChange={(c) => setRemember(!!c)}
              className="rounded-sm"
              data-testid="remember-me"
            />
            <label htmlFor="remember" className="text-xs text-stone-500 cursor-pointer select-none">Beni hatırla</label>
          </div>

          <Button type="submit" disabled={loading} className="w-full rounded-none bg-stone-900 hover:bg-[#1B4F72] font-mono text-xs uppercase tracking-widest h-11" data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Giriş Yap <ArrowRight className="w-3.5 h-3.5 ml-2" /></>}
          </Button>
        </form>
        <p className="text-center text-sm text-stone-500 mt-4">
          Hesabınız yok mu? <Link to="/kayit" className="text-[#1B4F72] hover:underline font-medium">Ücretsiz Kayıt Olun</Link>
        </p>
      </div>
    </div>
  );
}
